import * as React from 'react';
import { Spinner } from 'reactstrap';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { useAnalyseCSV, AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';

interface UploadStepProps {
    onComplete: (result: AnalysisResult) => void;
}

const UploadStep: React.FC<UploadStepProps> = ({ onComplete }) => {
    const t = useTranslations();
    const { run, isAnalysing, error } = useAnalyseCSV();
    const [isDragging, setIsDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFile = React.useCallback(async (file: File) => {
        const result = await run(file);
        if (result) onComplete(result);
    }, [run, onComplete]);

    const handleDrop = React.useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const handleDragOver = React.useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = React.useCallback(() => setIsDragging(false), []);

    const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    }, [handleFile]);

    const dropZoneStyle: React.CSSProperties = {
        border: `var(--border-medium) dashed ${isDragging ? 'var(--appBlue)' : 'var(--appLigthGrey)'}`,
        background: isDragging ? 'var(--bg-hover-light)' : 'var(--bg-light)',
        borderRadius: '8px',
        padding: 'var(--spacer-L)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--spacer-S)',
        cursor: isAnalysing ? 'default' : 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        textAlign: 'center',
        minHeight: '220px',
        justifyContent: 'center',
    };

    return (
        <div>
            <div
                style={dropZoneStyle}
                onClick={isAnalysing ? undefined : () => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                {isAnalysing ? (
                    <>
                        <Spinner color="primary" />
                        <span style={{ color: 'var(--text-muted)' }}>
                            {t(TK.importAppointmentsAnalysing)}
                        </span>
                    </>
                ) : (
                    <>
                        <CloudUploadIcon style={{ fontSize: '3rem', color: 'var(--text-muted)' }} />
                        <span style={{ color: 'var(--text-body)', fontWeight: 500 }}>
                            {t(TK.importAppointmentsUploadPrompt)}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            {t(TK.importAppointmentsUploadAccept)}
                        </span>
                    </>
                )}
            </div>
            {error && (
                <div style={{ marginTop: 'var(--spacer-S)', color: 'var(--danger-color)', fontSize: '0.9rem' }}>
                    {t(TK.importAppointmentsUploadError)}: {error}
                </div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls,.ods"
                style={{ display: 'none' }}
                onChange={handleInputChange}
            />
        </div>
    );
};

export default UploadStep;
